import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useMemo, useRef, useEffect } from "react";
import { ArrowRight, ArrowUpRight, ChevronRight, Search as SearchIcon, X } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { ALL_CATEGORIES_DIRECTORY } from "../data/appCategoryData";

const STATS = [
  { value: "53+", label: "Dedicated category pages" },
  { value: "320+", label: "Apps ranked across verticals" },
  { value: "12", label: "Industry groups" },
  { value: "#1", label: "Avg. category ranking goal" },
];

export default function AllCategoriesPage() {
  const [q, setQ] = useState("");
  const [activeGroup, setActiveGroup] = useState("All");
  const gridSectionRef = useRef(null);
  const filterBarRef = useRef(null);
  const isFirstRender = useRef(true);

  const groups = useMemo(() => {
    const set = new Set(ALL_CATEGORIES_DIRECTORY.map((c) => c.group));
    return ["All", ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    return ALL_CATEGORIES_DIRECTORY.filter((c) => {
      const matchesGroup = activeGroup === "All" || c.group === activeGroup;
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q.toLowerCase()) ||
        c.desc.toLowerCase().includes(q.toLowerCase()) ||
        c.group.toLowerCase().includes(q.toLowerCase());
      return matchesGroup && matchesQuery;
    });
  }, [q, activeGroup]);

  // When the filter or search changes, scroll the grid to a position where
  // the first row is fully visible below the sticky filter bar — but only if
  // the user has scrolled past the grid top (so we don't yank them around on first load).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!gridSectionRef.current || !filterBarRef.current) return;

    const filterBarHeight = filterBarRef.current.offsetHeight;
    const navbarHeight = window.innerWidth >= 768 ? 80 : 64; // matches top-16 / md:top-20
    const offset = navbarHeight + filterBarHeight + 16; // breathing room

    const gridTop =
      gridSectionRef.current.getBoundingClientRect().top + window.scrollY;
    const targetY = gridTop - offset;

    // Only scroll up if the user is below the grid top — never auto-scroll downward.
    if (window.scrollY > targetY) {
      window.scrollTo({ top: targetY, behavior: "smooth" });
    }
  }, [q, activeGroup]);

  return (
    <>
      <Helmet>
        <title>50+ App Categories — Dedicated ASO Pages for Every Vertical | Lumotis</title>
        <meta
          name="description"
          content="Browse 50+ dedicated App Store Optimization landing pages — Gaming, FinTech, Health, EdTech, eCommerce and more. Every app category gets its own ASO playbook."
        />
        <link rel="canonical" href="https://lumotis.com/app-categories" />
      </Helmet>

      <Navbar />

      <main data-testid="all-categories-page">

        {/* HERO */}
        <section className="relative bg-white pt-28 md:pt-32 pb-16 md:pb-20 overflow-hidden border-b border-zinc-200">
          <div className="absolute inset-0 bg-grid-pattern opacity-70 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/10 via-transparent to-transparent pointer-events-none" />

          <div className="container relative">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-8"
            >
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-charcoal">App Categories</span>
            </nav>

            <p className="text-overline text-electric mb-4">App Categories</p>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-4xl sm:text-5xl lg:text-[64px] font-extrabold leading-[1.05] tracking-tight text-charcoal text-balance max-w-4xl"
            >
              50+ app categories.{" "}
              <span className="relative inline-block">
                <span className="relative z-10">One ASO agency.</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
              </span>
            </motion.h1>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-3xl">
              Every app vertical we operate in has its own dedicated ASO landing page —
              with the keyword stack, competitor map, and case study tuned to that
              category. Pick yours below.
            </p>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 max-w-4xl">
              {STATS.map((s) => (
                <div key={s.label} className="bg-white p-5 md:p-6">
                  <p className="font-display text-3xl font-extrabold text-charcoal leading-none">
                    {s.value}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-widest text-zinc-500 leading-snug">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FILTER BAR (sticky) */}
        <section
          ref={filterBarRef}
          className="py-5 bg-white border-b border-zinc-200 sticky top-16 md:top-20 z-30 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
        >
          <div className="container flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* Group pills */}
            <div className="flex flex-wrap gap-2 overflow-x-auto">
              {groups.map((g) => (
                <button
                  key={g}
                  onClick={() => setActiveGroup(g)}
                  className={`px-3.5 py-1.5 text-xs font-semibold border transition-colors uppercase tracking-widest whitespace-nowrap ${
                    activeGroup === g
                      ? "bg-charcoal text-white border-charcoal"
                      : "border-zinc-300 text-zinc-700 hover:border-charcoal"
                  }`}
                  data-testid={`category-group-${g.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {g}
                </button>
              ))}
            </div>

            {/* Search */}
            <label className="flex items-center gap-2 border border-zinc-300 px-3 py-2 bg-white focus-within:border-electric md:min-w-[260px]">
              <SearchIcon className="w-4 h-4 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search categories…"
                className="outline-none bg-transparent text-sm w-full"
                aria-label="Search app categories"
                data-testid="categories-search"
              />
              {q && (
                <button onClick={() => setQ("")} className="text-zinc-400 hover:text-charcoal">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </label>
          </div>
        </section>

        {/* GRID */}
        <section ref={gridSectionRef} className="py-12 md:py-16 bg-white scroll-mt-40">
          <div className="container">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-zinc-500">
                Showing <span className="font-bold text-charcoal">{filtered.length}</span> of{" "}
                {ALL_CATEGORIES_DIRECTORY.length} categories
                {(q || activeGroup !== "All") && (
                  <button
                    onClick={() => {
                      setQ("");
                      setActiveGroup("All");
                    }}
                    className="ml-3 text-electric font-semibold hover:underline"
                  >
                    Clear filters
                  </button>
                )}
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="border border-dashed border-zinc-300 py-20 text-center">
                <p className="text-zinc-500 mb-4">No categories match your search.</p>
                <button
                  onClick={() => {
                    setQ("");
                    setActiveGroup("All");
                  }}
                  className="text-sm font-semibold text-electric hover:underline"
                >
                  Reset filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200">
                {filtered.map((c, idx) => (
                  <motion.div
                    key={c.slug}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.25, delay: idx * 0.015 }}
                  >
                    <Link
                      to={`/aso/${c.slug}`}
                      className="bg-white p-5 md:p-6 flex flex-col h-full hover:bg-charcoal hover:text-white transition-colors group min-h-[180px]"
                      data-testid={`category-card-${c.slug}`}
                    >
                      <span className="text-3xl mb-4 block leading-none" aria-hidden>{c.emoji}</span>
                      <h3 className="font-display text-base font-bold text-charcoal group-hover:text-white leading-snug">
                        {c.name}
                      </h3>
                      <p className="text-xs text-zinc-500 group-hover:text-zinc-400 mt-1.5 leading-snug line-clamp-2">
                        {c.desc}
                      </p>
                      <span className="mt-auto pt-4 inline-flex items-center gap-1 text-[10px] font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest">
                        ASO playbook <ArrowUpRight className="w-3 h-3" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA strip */}
        <section className="py-16 md:py-20 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">Don't see your vertical?</p>
                <h2 className="font-display text-2xl md:text-4xl font-extrabold text-charcoal leading-[1.1]">
                  We've shipped ASO across more verticals than any 50-page list can fit.
                </h2>
                <p className="mt-4 text-zinc-600 leading-relaxed max-w-2xl">
                  If your category isn't here, we've still probably ranked one. Tell us
                  about your app and we'll come back with a tailored ASO plan within 24h.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/free-aso-audit"
                  className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-[#42A5F5] hover:-translate-y-0.5 transition-all shadow-lg rounded-xl"
                >
                  Get a tailored plan <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
